import React, { useContext, useState } from "react";
import { ReportContext } from "@sscs/report/report";
import { REPORT_EXPORTS, isErrorMessage } from "@sscs/types";
import { Button, List, Menu, QueryResult, Tooltip } from "@sscs/ui";
import {
  ChevronLeftIcon as MUIChevronLeftIcon,
  CloudDownloadIcon as MUICloudDownloadOutlinedIcon,
  DownloadIcon as MUIFileDownloadOutlinedIcon,
} from "@sscs/ui/icons";
import { getQueryStatus } from "@sscs/utils";
import { TimeStatus } from "Components";
import { REPORT_EXPORT_HELPER_LABEL } from "Constants";
import { useDialog } from "Hooks";
import { ActionItem, FileType } from "Types";
import { useGetReportArchiveQuery } from "../../services/download.api";
import { ReportArchiveEntry } from "../../types";
import DownloadDialog from "../DownloadDialog/DownloadDialog";
import RlSafeExportDialog from "../RlSafeExportDialog/RlSafeExportDialog";
import styles from "./reportExportButton.module.scss";

type ExportAction = ActionItem & {
  actionType: "download_package" | "export_report" | "export_safe";
};

type ExportActionProps = {
  action: string;
  label: string;
};

const ExportAction = ({
  action,
  label,
}: ExportActionProps): React.JSX.Element => {
  return (
    <>
      <span>{action}</span>
      <div className={styles["item-label"]}>{label}</div>
    </>
  );
};

// TODO: see if ExportButton can be done as presentational only component

type ExportButtonProps = {
  versionId: string;
  isRepro: boolean;
  fileType: FileType;
  className?: string;
};

type SafeReportOption = {
  key: string;
  label: string;
  entry: ReportArchiveEntry;
};

function ExportButton({
  versionId,
  isRepro,
  fileType,
  className,
}: ExportButtonProps): React.JSX.Element | null {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [safeSubmenuAnchorEl, setSafeSubmenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const [selectedSafeReportId, setSelectedSafeReportId] = useState<
    string | null
  >(null);

  const open = Boolean(anchorEl);

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSafeSubmenuAnchorEl(null);
  };

  const { activeExport, reportId, roles, onReportExport } =
    useContext(ReportContext);

  const downloadDialog = useDialog();
  const safeExportDialog = useDialog();

  const packageDownloadEnabled = roles.canDownloadPackage;
  const cveExportEnabled = roles.canExportCve;
  const cycloneDxExportEnabled = roles.canExportCycloneDx;
  const jsonExportEnabled = roles.canExportJson;
  const safeExportEnabled = roles.canExportSafe;
  const sarifExportEnabled = roles.canExportSarif;
  const spdxExportEnabled = roles.canExportSpdx;
  const summaryPdfExportEnabled = roles.canExportSummaryPdf;
  const uriExportEnabled = roles.canExportUri;

  const {
    data: reportArchive,
    isLoading: isReportArchiveLoading,
    isFetching: isReportArchiveFetching,
    isError: isReportArchiveError,
    error: reportArchiveError,
  } = useGetReportArchiveQuery(
    { report_id: reportId },
    { skip: !open || !safeExportEnabled || !reportId },
  );

  const reportArchiveStatus = getQueryStatus({
    isLoading: isReportArchiveLoading,
    isFetching: isReportArchiveFetching,
    isError: isReportArchiveError,
    error: reportArchiveError,
    data: reportArchive,
  });

  const reportArchiveErrorMessage = isErrorMessage(reportArchiveError)
    ? reportArchiveError.data.error
    : "Could not load the report archive.";

  if (
    !packageDownloadEnabled &&
    !cveExportEnabled &&
    !cycloneDxExportEnabled &&
    !jsonExportEnabled &&
    !safeExportEnabled &&
    !sarifExportEnabled &&
    !spdxExportEnabled &&
    !summaryPdfExportEnabled &&
    !uriExportEnabled
  )
    return null;

  const generateMenuItems = (): ExportAction[] => {
    const items: ExportAction[] = [];

    if (packageDownloadEnabled) {
      items.push({
        id: "report-sharing-banner-download-file",
        type: "DOWNLOAD",
        actionType: "download_package",
        text: "Download package",
        itemIcon: <MUICloudDownloadOutlinedIcon />,
        onClick: () => downloadDialog.open(),
      });
    }

    if (cycloneDxExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-cyclonedx",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.cycloneDX}`}
            label={REPORT_EXPORT_HELPER_LABEL.cycloneDX}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.cycloneDX,
        onClick: () => onReportExport?.("cycloneDX"),
      });
    }

    if (spdxExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-spdx",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.spdx}`}
            label={REPORT_EXPORT_HELPER_LABEL.spdx}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.spdx,
        onClick: () => onReportExport?.("spdx"),
      });
    }

    if (sarifExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-sarif",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.sarif}`}
            label={REPORT_EXPORT_HELPER_LABEL.sarif}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.sarif,
        onClick: () => onReportExport?.("sarif"),
      });
    }

    if (cveExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-cve",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.cve}`}
            label={REPORT_EXPORT_HELPER_LABEL.cve}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.cve,
        onClick: () => onReportExport?.("cve"),
      });
    }

    if (uriExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-uri",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.uri}`}
            label={REPORT_EXPORT_HELPER_LABEL.uri}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.uri,
        onClick: () => onReportExport?.("uri"),
      });
    }

    if (jsonExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-json",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.json}`}
            label={REPORT_EXPORT_HELPER_LABEL.json}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.json,
        onClick: () => onReportExport?.("json"),
      });
    }

    if (summaryPdfExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-summary-pdf",
        type: "CUSTOM",
        actionType: "export_report",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.summaryPdf}`}
            label={REPORT_EXPORT_HELPER_LABEL.summaryPdf}
          />
        ),
        itemIcon: <MUIFileDownloadOutlinedIcon />,
        isDisabled: activeExport.summaryPdf,
        onClick: () => onReportExport?.("summaryPdf"),
      });
    }

    if (safeExportEnabled) {
      items.push({
        id: "report-sharing-banner-export-as-safe",
        type: "CUSTOM",
        actionType: "export_safe",
        text: (
          <ExportAction
            action={`Export ${REPORT_EXPORTS.safe}`}
            label={REPORT_EXPORT_HELPER_LABEL.safe}
          />
        ),
        onClick: () => {},
      });
    }

    return items;
  };

  const menuItems = generateMenuItems();

  const safeReportOptions: SafeReportOption[] = [];
  if (reportArchive?.latest) {
    safeReportOptions.push({
      key: "latest",
      label: "Latest scan",
      entry: reportArchive.latest,
    });
  }
  if (reportArchive?.released) {
    safeReportOptions.push({
      key: "released",
      label: "When released",
      entry: reportArchive.released,
    });
  }
  if (reportArchive?.first) {
    safeReportOptions.push({
      key: "first",
      label: "When uploaded",
      entry: reportArchive.first,
    });
  }

  const handleSafeReportSelect = (reportIdToExport: string) => {
    setSelectedSafeReportId(reportIdToExport);
    safeExportDialog.open();
    handleCloseMenu();
  };

  return (
    <>
      <DownloadDialog
        onClose={downloadDialog.close}
        isOpen={downloadDialog.isOpen}
        fileID={versionId}
        fileType={fileType}
        isRepro={isRepro}
      />
      <RlSafeExportDialog
        onClose={() => {
          safeExportDialog.close();
          setSelectedSafeReportId(null);
        }}
        reportId={selectedSafeReportId ?? reportId}
        isOpen={safeExportDialog.isOpen}
      />
      <Button
        type="button"
        color="secondary"
        onClick={handleOpenMenuClick}
        className={className}
      >
        Export
      </Button>
      <Menu.Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
        {
          // returning array because Menu doesn't accept fragments: https://github.com/mui/material-ui/issues/16181
          menuItems.flatMap((action, index) => {
            let menuItemNode;
            if (action.actionType === "export_safe") {
              const trigger = (
                <Menu.Item
                  id={action.id}
                  key={action.id}
                  disabled={isReportArchiveError}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(safeSubmenuAnchorEl)}
                  onMouseEnter={(event) =>
                    setSafeSubmenuAnchorEl(event.currentTarget)
                  }
                  onClick={(event) =>
                    setSafeSubmenuAnchorEl(event.currentTarget)
                  }
                  className={styles["menu-item"]}
                >
                  <List.ItemIcon className={styles["menu-icon"]}>
                    <MUIChevronLeftIcon />
                  </List.ItemIcon>
                  <List.ItemText>{action.text}</List.ItemText>
                </Menu.Item>
              );
              menuItemNode = isReportArchiveError ? (
                <Tooltip key={action.id} title={reportArchiveErrorMessage}>
                  <span className={styles["submenu-trigger"]}>{trigger}</span>
                </Tooltip>
              ) : (
                trigger
              );
            } else {
              menuItemNode = (
                <Menu.Item
                  id={action.id}
                  key={action.id}
                  disabled={action.isDisabled}
                  onMouseEnter={() => setSafeSubmenuAnchorEl(null)}
                  onClick={() => {
                    action.onClick();
                    handleCloseMenu();
                  }}
                  className={styles["menu-item"]}
                >
                  <List.ItemIcon className={styles["menu-icon"]}>
                    {action.itemIcon}
                  </List.ItemIcon>
                  <List.ItemText>{action.text}</List.ItemText>
                </Menu.Item>
              );
            }
            const previousActionType = menuItems[index - 1]?.actionType;
            const startsNewGroup =
              previousActionType !== undefined &&
              previousActionType !== action.actionType;

            return startsNewGroup
              ? [
                  <Menu.Divider key={`divider-before-${action.id}`} />,
                  menuItemNode,
                ]
              : [menuItemNode];
          })
        }
      </Menu.Menu>
      <Menu.Menu
        anchorEl={safeSubmenuAnchorEl}
        open={Boolean(safeSubmenuAnchorEl) && !isReportArchiveError}
        onClose={() => setSafeSubmenuAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          root: { className: styles["submenu-overlay"] },
          paper: { className: styles["submenu-surface"] },
        }}
      >
        <QueryResult status={reportArchiveStatus}>
          {safeReportOptions.map((option) => (
            <Menu.Item
              id={`report-sharing-banner-export-as-safe-${option.key}`}
              key={option.key}
              onClick={() => handleSafeReportSelect(option.entry.report_id)}
              className={styles["menu-item"]}
            >
              <List.ItemIcon className={styles["menu-icon"]}>
                <MUIFileDownloadOutlinedIcon />
              </List.ItemIcon>
              <List.ItemText>
                {option.label}
                <div className={styles["item-label"]}>
                  <TimeStatus timestamp={option.entry.created} />
                </div>
              </List.ItemText>
            </Menu.Item>
          ))}
        </QueryResult>
      </Menu.Menu>
    </>
  );
}

export default ExportButton;
